import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import noop from 'lodash-es/noop';
import moment from 'moment-timezone';
import createQueryParams from '../Client/createQueryParams';
import { Resource } from '../Resource';
import { timestampToMoment } from '../core/history';
import { nullToUndefined, undefinedToNull } from '../core/utils';
import * as methods from '../methods';
import { isRawSeedCornCharacteristics, isRawSorghoCharacteristics, isRawSweetCornCharacteristics, isSeedCornCharacteristics, isSorghoCharacteristics, isSweetCornCharacteristics } from './waterBalanceArvalis.type';
export const isArvalisPlotSummary = (val) => {
    return 'plotIsSetupForCurrentSeason' in val;
};
const rootPath = 'wbarvalis/waterbalance/v1';
const CONTRACT_CODE = 'CONTRAT_WEENAT_t5R26pu';
const toArvalisDateString = (date, separator) => {
    const separatorToUse = separator || '/';
    const template = ['DD', 'MM', 'YYYY'].join(separatorToUse);
    return moment(date).format(template);
};
export const fromArvalisDateString = (date, separator) => {
    const separatorToUse = separator || '/';
    const template = ['DD', 'MM', 'YYYY'].join(separatorToUse);
    return moment(date, template).toDate();
};
const toGenericInfosRaw = (genericInfos) => {
    const { city, firstName, lastName, mail, zipCode } = genericInfos;
    return { cp: zipCode, mail, ville: city, prenom: firstName, nom: lastName };
};
const toGenericInfos = (genericInfosRaw) => {
    const { mail, nom, prenom, ville, cp } = genericInfosRaw;
    return { mail, city: ville, firstName: prenom, lastName: nom, zipCode: cp };
};
const fromClientRaw = (raw) => ({
    clientId: raw.code_client,
    ...toGenericInfos(raw)
});
const toClientRaw = (client) => ({
    code_client: client.clientId,
    ...toGenericInfosRaw(client)
});
const fromTechRaw = (raw) => ({
    ...fromClientRaw(raw),
    techId: raw.code_technicien
});
const toTechRaw = (tech) => ({
    ...toClientRaw(tech),
    code_technicien: tech.techId
});
const fromRawCipan = (raw) => ({
    startDate: fromArvalisDateString(raw.implantation),
    raisingDate: fromArvalisDateString(raw.levee),
    destructionDate: fromArvalisDateString(raw.destruction),
    development: raw.developpement
});
const toRawCipan = (cipan) => ({
    implantation: toArvalisDateString(cipan.startDate),
    levee: toArvalisDateString(cipan.raisingDate),
    destruction: toArvalisDateString(cipan.destructionDate),
    developpement: cipan.development
});
const fromRawCharacteristics = (raw) => {
    if (isRawSorghoCharacteristics(raw)) {
        return { variety: raw.variete, precocity: raw.precocite };
    }
    else if (isRawSweetCornCharacteristics(raw)) {
        return { variety: raw.variete, supersweet: raw.supersweet === 'oui' };
    }
    else if (isRawSeedCornCharacteristics(raw)) {
        return {
            numberOfDaysBtwSowingFlowering: nullToUndefined(raw.nb_jour_semis_flor),
            gddBtwSowingFlowering: nullToUndefined(raw.somme_temp_semis_flor),
            size: raw.gabarit,
            femaleGenitor: nullToUndefined(raw.geniteur_femelle),
            maleGenitor: nullToUndefined(raw.geniteur_male),
            m1SowingDate: !isNil(raw.date_semis_m1)
                ? fromArvalisDateString(raw.date_semis_m1)
                : undefined,
            m2SowingDate: !isNil(raw.date_semis_m2)
                ? fromArvalisDateString(raw.date_semis_m2)
                : undefined,
            m3SowingDate: !isNil(raw.date_semis_m3)
                ? fromArvalisDateString(raw.date_semis_m3)
                : undefined,
            sowingDevice: nullToUndefined(raw.dispositif_semis)
        };
    }
    else {
        return {
            variety: raw.variete
        };
    }
};
const toRawCharacteristics = (cc) => {
    if (isSorghoCharacteristics(cc)) {
        return { variete: cc.variety, precocite: cc.precocity };
    }
    else if (isSweetCornCharacteristics(cc)) {
        return { variete: cc.variety, supersweet: cc.supersweet ? 'oui' : 'non' };
    }
    else if (isSeedCornCharacteristics(cc)) {
        return {
            nb_jour_semis_flor: cc.numberOfDaysBtwSowingFlowering,
            somme_temp_semis_flor: cc.gddBtwSowingFlowering,
            gabarit: cc.size,
            geniteur_femelle: undefinedToNull(cc.femaleGenitor),
            geniteur_male: undefinedToNull(cc.maleGenitor),
            date_semis_m1: !isNil(cc.m1SowingDate) ? toArvalisDateString(cc.m1SowingDate) : null,
            date_semis_m2: !isNil(cc.m2SowingDate) ? toArvalisDateString(cc.m2SowingDate) : null,
            date_semis_m3: !isNil(cc.m3SowingDate) ? toArvalisDateString(cc.m3SowingDate) : null,
            dispositif_semis: undefinedToNull(cc.sowingDevice)
        };
    }
    else {
        return {
            variete: cc.variety
        };
    }
};
const fromWaterBalanceEntryRaw = (raw) => ({
    RFU: raw.RFU,
    RU: raw.RU,
    date: fromArvalisDateString(raw.Date),
    etp: raw.ETP,
    profileDrainage: raw['Drainage profil'],
    rainfalls: raw.Pluie,
    watering: raw.Irrigation,
    rootDrainage: raw['Drainage racinaire'],
    survivalReserve: raw['Reserve survie'],
    waterDeficit: raw['Deficit en eau du sol']
});
const fromRaw = {
    getSeasonStressAssessment: (rawResult) => rawResult.map((raw) => ({
        drainAccumulation: raw.drainage,
        endDate: fromArvalisDateString(raw.date_fin),
        startDate: fromArvalisDateString(raw.date_debut),
        endingStage: raw.fin,
        startingStage: raw.debut,
        irrigationsAccumulation: raw.irrigation,
        needCoverage: raw['etr/etm'],
        numberOfStressDays: raw.nb_jours_stress,
        rainfallsAccumulation: raw.pluie
    })),
    getPreviousCropOptions: (raw) => ({
        notIrrigated: raw.non_irrigue,
        irrigated: raw.irrigue
    }),
    constructors: (raw) => raw.map((constructor) => {
        return { label: constructor.name, value: constructor.id };
    }),
    soil: (x) => ({
        soilCode: x.Code_Sol,
        region: x.Region,
        arvalisName: x.Nom_ARVALIS,
        officialName: x.Nom_officiel,
        referential: x.Correspondance_referenciel_pedo_fr,
        limestone: x.Calcaire,
        pierrosite: x.Pierrosite,
        texture: x.Texture,
        depth: x.Profondeur,
        hydromorphy: x.Hydromorphie
    }),
    soils: (raw) => ({
        filters: raw.filters,
        results: raw.results.map(fromRaw.soil)
    }),
    calibrationDeficit: (rawResult) => rawResult.map((raw) => ({
        date: fromArvalisDateString(raw.date),
        deficit: raw.deficit
    })),
    client: fromClientRaw,
    tech: fromTechRaw,
    farmer: (raw) => ({
        ...fromClientRaw(raw),
        farmerId: raw.code_exploitant,
        techIds: raw.code_technicien.techs,
        consent_url: raw.url_consentement
    }),
    plot: (raw) => ({
        zipCode: nullToUndefined(raw.code_postal),
        clientId: raw.code_client,
        cropType: raw.libelle_culture,
        cropCharacteristics: fromRawCharacteristics(raw.caracteristiques_culture),
        farmerId: raw.code_exploitant,
        inseeCode: raw.code_insee,
        latitude: raw.latitude,
        longitude: raw.longitude,
        plotArea: nullToUndefined(raw.surface),
        daysBetweenWatering: raw.duree_tour_eau,
        plotId: raw.code_parcelle,
        wateringAmount: nullToUndefined(raw.dose_irrig),
        plotName: nullToUndefined(raw.nom_parcelle),
        previousCrop: raw.precedent,
        previousWatering: raw.irrig_precedent === 'oui',
        sowingDate: fromArvalisDateString(raw.date_semis),
        cipan: !isNil(raw.cipan) ? fromRawCipan(raw.cipan) : undefined,
        soilCode: nullToUndefined(raw.codesol),
        device: nullToUndefined(raw.station),
        quota: nullToUndefined(raw.quota),
        rfuMax: nullToUndefined(raw.rfu_max),
        ruMax: nullToUndefined(raw.ru_max),
        wateringDevice: nullToUndefined(raw.materiel_irrig),
        station: nullToUndefined(raw.station)
    }),
    results: (raw) => {
        const rawEntry = Object.values(raw)[0];
        if (rawEntry === undefined) {
            throw new Error(`arvalis result empty ${JSON.stringify(raw)}`);
        }
        return {
            computationDate: !isNil(rawEntry.date_calcul)
                ? moment(rawEntry.date_calcul, 'YYYY-MM-DD HH:mm:ss.SSSSSSZ').toDate()
                : undefined,
            lastForecastDate: !isNil(rawEntry.date_fin_donnees_prevision)
                ? moment(rawEntry.date_fin_donnees_prevision, 'YYYY-MM-DD HH:mm:ssZ').toDate()
                : undefined,
            lastMeasureDate: !isNil(rawEntry.date_fin_donnees_reelles)
                ? moment(rawEntry.date_fin_donnees_reelles, 'YYYY-MM-DD HH:mm:ssZ').toDate()
                : undefined,
            waterBalance: rawEntry.bilan_hydrique.map(fromWaterBalanceEntryRaw),
            status: rawEntry.statut_calcul === 'erreur' ? 'error' : rawEntry.statut_calcul,
            waterBalanceEnd: !isNil(rawEntry.date_fin_bilan)
                ? moment(rawEntry.date_fin_bilan, 'YYYY-MM-DD').toDate()
                : undefined,
            waterBalanceStart: !isNil(rawEntry.date_debut_bilan)
                ? moment(rawEntry.date_debut_bilan, 'YYYY-MM-DD').toDate()
                : undefined
        };
    },
    irrigations: (raw) => raw,
    rainfalls: (raw) => raw,
    stages: (raw) => raw.map((stageRaw) => ({
        code: stageRaw.code,
        date: fromArvalisDateString(stageRaw.date),
        origin: stageRaw.origin
    })),
    varietes: (raw) => raw.map((rawVariety) => ({
        code: rawVariety.code_variete,
        name: rawVariety.nom_variete,
        type: rawVariety.type_variete
    })),
    irrigationPeriods: (raw) => ({
        irrigationPeriod: raw.irrigation_period,
        sensitivityPeriod: raw.sensitivity_period
    })
};
const toRaw = {
    client: toClientRaw,
    tech: toTechRaw,
    farmer: (farmer) => ({
        ...toClientRaw(farmer),
        code_exploitant: `${farmer.farmerId}`,
        code_technicien: { techs: farmer.techIds }
    }),
    plot: (plot) => ({
        caracteristiques_culture: toRawCharacteristics(plot.cropCharacteristics),
        cipan: !isNil(plot.cipan) ? toRawCipan(plot.cipan) : null,
        code_client: plot.clientId,
        code_contrat: CONTRACT_CODE,
        code_exploitant: `${plot.farmerId}`,
        code_insee: plot.inseeCode,
        code_parcelle: `${plot.plotId}`,
        code_postal: undefinedToNull(plot.zipCode),
        codesol: undefinedToNull(plot.soilCode),
        date_semis: toArvalisDateString(plot.sowingDate),
        dose_irrig: undefinedToNull(plot.wateringAmount),
        duree_tour_eau: plot.daysBetweenWatering,
        irrig_precedent: plot.previousWatering ? 'oui' : 'non',
        latitude: plot.latitude,
        longitude: plot.longitude,
        libelle_culture: plot.cropType,
        materiel_irrig: undefinedToNull(plot.wateringDevice),
        nom_parcelle: undefinedToNull(plot.plotName),
        precedent: plot.previousCrop,
        quota: undefinedToNull(plot.quota),
        rfu_max: undefinedToNull(plot.rfuMax),
        ru_max: undefinedToNull(plot.ruMax),
        station: undefinedToNull(plot.device),
        surface: undefinedToNull(plot.plotArea)
    })
};
const toRecordStringString = (data) => {
    const result = {};
    Object.entries(data).forEach(([key, value]) => {
        let valueAsString = '';
        if (!isNil(value)) {
            if (typeof value === 'number') {
                valueAsString = `${value}`;
            }
            else if (typeof value === 'string') {
                valueAsString = value;
            }
            else if (typeof value === 'object' || Array.isArray(value)) {
                valueAsString = JSON.stringify(value);
            }
        }
        if (!isEmpty(valueAsString)) {
            result[key] = valueAsString;
        }
    });
    return result;
};
const waterBalanceArvalis = Resource((requesterBuilder, config) => {
    const defaultCtx = {
        host: config.getCtx().hosts.ts,
        requesterBuilder,
        headers: {
            'content-type': 'application/x-www-form-urlencoded'
        },
        bodySerializer: (data) => createQueryParams(toRecordStringString(data)).slice(1)
    };
    /**
     * Get the water balance results
     */
    const getResult = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/results/plots/${query.plotId}`,
        fromRaw: fromRaw.results,
        params: {
            query: {
                code_client: query.clientId,
                histo: query.history === true ? 1 : undefined,
                start: !isNil(query.start) ? toArvalisDateString(query.start, '-') : undefined,
                end: !isNil(query.end) ? toArvalisDateString(query.end, '-') : undefined
            }
        },
        cacheConfig: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            retry: (count, error) => {
                return count < 4;
            }
        }
    })();
    /**
     * Get the rainfalls for a given arvalis plot
     */
    const getRainfalls = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/plots/${query.plotId}/rainfalls/all`,
        fromRaw: fromRaw.rainfalls,
        params: {
            query: {
                code_client: query.clientId,
                histo: query.history === true ? 1 : undefined
            }
        }
    })();
    const buildKeysToInvalidateOnRainfallsEdition = (query) => () => {
        return [getResult(query).invalidationKey, getRainfalls(query).invalidationKey];
    };
    /**
     * Add a rainfall report to a given arvalis plot
     */
    const addRainfall = (query) => methods.create({
        ...defaultCtx,
        method: 'PATCH',
        rootPath: `${rootPath}/plots/${query.plotId}/rainfalls`,
        buildKeysToInvalidate: buildKeysToInvalidateOnRainfallsEdition(query),
        fromRaw: () => undefined,
        parse: async (x) => x,
        toRaw: (form) => ({
            code_client: query.clientId,
            pluviometrie: [
                { date: timestampToMoment(form.timestamp).format('DD/MM/YYYY'), dose: form.value }
            ]
        })
    })();
    const deleteRainfalls = (query) => methods.create({
        ...defaultCtx,
        method: 'PATCH',
        rootPath: `${rootPath}/plots/${query.plotId}/rainfalls`,
        fromRaw: () => undefined,
        parse: async (x) => x,
        buildKeysToInvalidate: buildKeysToInvalidateOnRainfallsEdition(query),
        toRaw: (form) => ({
            code_client: query.clientId,
            pluviometrie: [{ date: timestampToMoment(form.timestamp).format('DD/MM/YYYY'), dose: null }]
        })
    })();
    /**
     * Get the irrigations for a given arvalis plot
     */
    const getIrrigations = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/plots/${query.plotId}/irrigations`,
        fromRaw: fromRaw.irrigations,
        params: {
            query: {
                code_client: query.clientId,
                histo: query.history === true ? 1 : undefined
            }
        }
    })();
    const buildKeysToInvalidateOnIrrigationsEdition = (query) => () => {
        return [getResult(query).invalidationKey, getIrrigations(query).invalidationKey];
    };
    /**
     * Add an irrigation to a given arvalis plot
     */
    const addIrrigation = (query) => methods.create({
        ...defaultCtx,
        method: 'PATCH',
        rootPath: `${rootPath}/plots/${query.plotId}/irrigations`,
        fromRaw: () => undefined,
        parse: async (x) => x,
        buildKeysToInvalidate: buildKeysToInvalidateOnIrrigationsEdition(query),
        toRaw: (form) => ({
            code_client: query.clientId,
            irrigation: [
                { date: timestampToMoment(form.timestamp).format('DD/MM/YYYY'), dose: form.value }
            ]
        })
    })();
    /**
     * Deletes an irrigation to a given arvalis plot
     */
    const deleteIrrigation = (query) => methods.create({
        ...defaultCtx,
        method: 'PATCH',
        rootPath: `${rootPath}/plots/${query.plotId}/irrigations`,
        fromRaw: () => undefined,
        parse: async (x) => x,
        buildKeysToInvalidate: buildKeysToInvalidateOnIrrigationsEdition(query),
        toRaw: (form) => ({
            code_client: query.clientId,
            irrigation: [{ date: timestampToMoment(form.timestamp).format('DD/MM/YYYY'), dose: null }]
        })
    })();
    /**
     * Get the irrigations for a given arvalis plot
     */
    const getIrrigationPeriods = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/irrigation_periods/${query.cropType}`,
        fromRaw: fromRaw.irrigationPeriods,
        params: {
            query: {
                code_client: query.clientId
            }
        }
    })();
    /**
     * Get the previous crops for a given arvalis plot
     */
    const getPreviousCropOptions = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/previous_crop`,
        fromRaw: fromRaw.getPreviousCropOptions,
        params: {
            query: {
                code_client: query.clientId
            }
        }
    })();
    /**
     * Get the stages from arvalis
     */
    const getStages = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/plots/${query.plotId}/stages`,
        fromRaw: fromRaw.stages,
        params: {
            query: {
                code_client: query.clientId,
                histo: query.history === true ? 1 : undefined
            }
        }
    })();
    /**
     * Get the stages from arvalis
     */
    const getCalibrationDeficit = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/plots/${query.plotId}/deficit_calibration`,
        fromRaw: fromRaw.calibrationDeficit,
        params: {
            query: {
                code_client: query.clientId
            }
        }
    })();
    /**
     * Get all constructors
     */
    const getConstructors = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/iot/constructors`,
        fromRaw: fromRaw.constructors,
        params: {
            query: {
                code_client: !isNil(query.clientId) ? query.clientId : undefined
            }
        }
    })();
    const getPlot = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/plots/${query.plotId}`,
        fromRaw: fromRaw.plot,
        params: {
            query: {
                code_client: query.clientId,
                histo: query.history === true ? 1 : undefined
            }
        }
    })();
    const deletePlot = (query) => methods.deletion({
        host: config.getCtx().hosts.ts,
        requesterBuilder,
        rootPath: `${rootPath}/plots`,
        params: {
            query: {
                code_client: query.clientId
            }
        }
    })(query.plotId);
    const getSeasonStressAssessment = (query) => methods.get({
        ...defaultCtx,
        rootPath: `${rootPath}/synthese/stress/plots/${query.plotId}`,
        fromRaw: fromRaw.getSeasonStressAssessment,
        params: {
            query: {
                code_client: query.clientId
            }
        }
    })();
    const getSummaries = (query) => methods.get({
        ...defaultCtx,
        rootPath: `wbarvalis-weenat/summary`,
        fromRaw: (x) => x,
        params: {
            query
        }
    })();
    return {
        addClient: methods.create({
            ...defaultCtx,
            rootPath: `${rootPath}/groups`,
            fromRaw: noop,
            toRaw: toRaw.client
        }),
        getClient: (query) => methods.get({
            ...defaultCtx,
            rootPath: `${rootPath}/groups`,
            fromRaw: fromRaw.client,
            params: {
                query: {
                    code_client: query.clientId
                }
            }
        })(),
        addTechnician: methods.create({
            ...defaultCtx,
            rootPath: `${rootPath}/techs`,
            fromRaw: noop,
            toRaw: toRaw.tech
        }),
        addFarmer: methods.create({
            ...defaultCtx,
            rootPath: `${rootPath}/farmers`,
            fromRaw: noop,
            toRaw: toRaw.farmer
        }),
        getFarmer: (query) => methods.get({
            ...defaultCtx,
            rootPath: `${rootPath}/farmers/${query.farmerId}`,
            fromRaw: fromRaw.farmer,
            params: {
                query: {
                    code_client: query.clientId
                }
            }
        })(),
        addPlot: (query) => methods.create({
            ...defaultCtx,
            rootPath: `${rootPath}/plots`,
            fromRaw: noop,
            toRaw: toRaw.plot,
            buildKeysToInvalidate: () => {
                return [
                    getPlot(query).key,
                    getResult(query).invalidationKey,
                    getSummaries({ clientCode: query.clientId, orgId: 0 }).invalidationKey
                ];
            }
        })(),
        getPlot,
        deletePlot,
        getConstructors,
        updatePlot: (query) => methods.update({
            ...defaultCtx,
            method: 'PATCH',
            rootPath: `${rootPath}/plots/${query.plotId}`,
            toRaw: toRaw.plot,
            // Because we're not receiving the modified plot in return but simply an object
            fromRaw: () => undefined,
            parse: async (x) => x,
            params: {
                query: {
                    code_client: query.clientId
                }
            },
            buildKeysToInvalidate: () => {
                return [getPlot(query).key];
            }
        })(),
        getResult,
        getIrrigations,
        addIrrigation,
        updateIrrigation: addIrrigation,
        deleteIrrigation,
        getIrrigationPeriods,
        getRainfalls,
        addRainfall,
        updateRainfall: addRainfall,
        deleteRainfalls,
        addStage: (query) => methods.update({
            ...defaultCtx,
            method: 'PATCH',
            rootPath: `${rootPath}/plots/${query.plotId}/obs_stages`,
            buildKeysToInvalidate: () => {
                return [getStages(query).key];
            },
            fromRaw: () => undefined,
            parse: async (x) => x,
            toRaw: (stages) => ({
                code_client: query.clientId,
                stades: stages.map((x) => {
                    const date = timestampToMoment(x.date.getTime() / 1000).format('DD/MM/YYYY');
                    return {
                        date,
                        code: x.code
                    };
                })
            })
        })(),
        getStages,
        getVarieties: (query) => {
            return methods.get({
                ...defaultCtx,
                rootPath: `${rootPath}/varieties/${query.cropType}`,
                fromRaw: fromRaw.varietes,
                params: {
                    query: {
                        code_client: query.clientId,
                        num_departement: query.cropType === 'PDT' ? query.departmentCode : undefined
                    }
                }
            })();
        },
        getPreviousCropOptions,
        /** Weeant custom method calls, this DOES NOT redirect call to arvalis api as a flat pass */
        getSoils: (query) => methods.get({
            ...defaultCtx,
            rootPath: `wbarvalis-weenat/soils`,
            fromRaw: fromRaw.soils,
            params: {
                query: {
                    region: query.region,
                    texture: query.texture,
                    limestone: query.limestone,
                    stoniness: query.stoniness,
                    depth: query.depth,
                    hydromorphy: query.hydromorphy
                }
            }
        })(),
        getSoil: (soilCode) => methods.get({
            ...defaultCtx,
            rootPath: `wbarvalis-weenat/soils/${soilCode}`,
            fromRaw: fromRaw.soil
        })(),
        getCaracteristicsSoil: (query) => methods.get({
            ...defaultCtx,
            rootPath: `${rootPath}/ru_rfu_soil`,
            fromRaw: (x) => x,
            params: {
                query: {
                    code_client: query.clientId,
                    codesol: query.soilCode,
                    num_departement: query.deptNumber,
                    code_culture: query.cropType,
                    date_semis: moment(query.sowingDate).format('DD/MM/YYYY')
                }
            }
        })(),
        getGeoloc: (query) => methods.get({
            ...defaultCtx,
            rootPath: `wbarvalis-weenat/geoloc`,
            fromRaw: (x) => x,
            params: {
                query: {
                    lat: query.lat,
                    lng: query.lng
                }
            }
        })(),
        getSummaries,
        getCalibrationDeficit,
        getSeasonStressAssessment
    };
});
export default waterBalanceArvalis;
